// The Academy's left navigator (the docs-site tree): a search box (⌘K), the
// "Courses" section with every course and the active course expanded to its
// lessons, and "Quick links". Rendered by AcademyLayout both in the sticky
// desktop column and inside the "Browse" panel below lg.
import { CheckCircleIcon, ChevronDownIcon, ChevronRightIcon, CursorArrowRaysIcon, LockClosedIcon, MagnifyingGlassIcon, MusicalNoteIcon, SparklesIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { RefObject, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ALL_LESSONS, COURSES, courseProgress, lessonsForCourse } from "../catalog";
import { Course, LessonDefinition } from "../catalog/types";
import { useAcademyProgress } from "../progress";
import { countUnseenNewLessons } from "../useAcademyBadge";
import { openWalkthrough, walkthroughAvailable } from "../walkthrough";
import { NewChip } from "./Chip";
import { CourseIcon } from "./courseIcons";
import { GATE_LABELS, useGateStatus } from "./gates";
import { isNewForUser, lessonPath } from "./LessonCard";

const match = (q: string, ...fields: (string | undefined)[]) => fields.some((f) => f && f.toLowerCase().includes(q));

/** The lesson "Start the walkthrough" shortcuts to: the first one with a tour. */
export const firstWalkthroughLesson = (): LessonDefinition | undefined => ALL_LESSONS.find((l) => l.tourId);
export const firstLesson = (): LessonDefinition | undefined => ALL_LESSONS[0];

const CourseRow: React.FC<{ course: Course; active: boolean; activeLessonId?: string; onNavigate?: () => void }> = ({ course, active, activeLessonId, onNavigate }) => {
  const { progress, isCompleted, isViewed } = useAcademyProgress();
  const { user } = useAuth();
  const gate = useGateStatus(course.gate);
  const lessons = lessonsForCourse(course.id);
  const p = courseProgress(course.id, progress);
  return (
    <li>
      <Link
        to={`/academy/${course.id}`}
        onClick={onNavigate}
        aria-current={active && !activeLessonId ? "page" : undefined}
        className={cn(
          "flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors",
          active ? "bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300 font-medium" : "text-base-content/80 hover:bg-base-200",
        )}
      >
        {active ? <ChevronDownIcon className="size-3.5 shrink-0 text-base-content/50" /> : <ChevronRightIcon className="size-3.5 shrink-0 text-base-content/40" />}
        <CourseIcon icon={course.icon} className={cn("size-4 shrink-0", active ? "text-blue-600 dark:text-blue-300" : "text-base-content/60")} />
        <span className="flex-1 min-w-0 truncate">{course.title}</span>
        {gate && <LockClosedIcon className={cn("size-3.5 shrink-0", gate.locked ? "text-amber-500" : "text-base-content/40")} aria-label={GATE_LABELS[gate.gate]} />}
        <span className={cn("shrink-0 text-[11px] tabular-nums", p.done === p.total && p.total > 0 ? "text-blue-500" : "text-base-content/50")}>
          {p.done}/{p.total}
        </span>
      </Link>
      {active && lessons.length > 0 && (
        <ol className="mt-0.5 mb-1 ml-[18px] border-l border-base-300 pl-2 space-y-px">
          {lessons.map((l) => {
            const current = l.id === activeLessonId;
            const done = isCompleted(l.id);
            const viewed = isViewed(l.id);
            return (
              <li key={l.id}>
                <Link
                  to={lessonPath(l)}
                  onClick={onNavigate}
                  aria-current={current ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-snug transition-colors",
                    current ? "bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300 font-medium" : "text-base-content/70 hover:bg-base-200 hover:text-base-content",
                  )}
                >
                  {done && <CheckCircleIcon className="size-3.5 shrink-0 text-blue-500" aria-label="Completed" />}
                  <span className="flex-1 min-w-0 truncate">{l.title}</span>
                  {isNewForUser(l, (user as any)?.created_at, viewed) && <NewChip />}
                </Link>
              </li>
            );
          })}
        </ol>
      )}
    </li>
  );
};

const AcademyNavigator: React.FC<{
  activeCourseId?: string;
  activeLessonId?: string;
  searchRef?: RefObject<HTMLInputElement>;
  /** Called after any link is followed (closes the mobile panel). */
  onNavigate?: () => void;
  className?: string;
}> = ({ activeCourseId, activeLessonId, searchRef, onNavigate, className }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const { progress } = useAcademyProgress();
  const createdAt = (user as any)?.created_at as string | undefined;
  // Same number as the sidebar pill: unopened lessons newer than the account.
  const newCount = useMemo(() => countUnseenNewLessons(ALL_LESSONS, createdAt, progress.viewed), [createdAt, progress.viewed]);
  const sampleCount = useMemo(() => ALL_LESSONS.filter((l) => l.sample).length, []);
  const walkthrough = firstWalkthroughLesson();
  const walkthroughReady = walkthroughAvailable(walkthrough?.tourId);

  const results = useMemo(() => {
    if (!q) return null;
    const courses = COURSES.filter((c) => match(q, c.title, c.blurb));
    const lessons = ALL_LESSONS.filter((l) => match(q, l.title, l.subtitle, l.summary, ...l.learn));
    return { courses, lessons };
  }, [q]);

  const go = (to: string) => {
    setQuery("");
    onNavigate?.();
    navigate(to);
  };

  const quickLink = "flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-base-content/80 hover:bg-base-200 transition-colors w-full text-left";

  return (
    <nav className={cn("flex flex-col gap-4 text-sm", className)} aria-label="Academy navigation">
      <div className="relative">
        <MagnifyingGlassIcon className="h-4 w-4 text-base-content/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setQuery("");
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          placeholder="Search lessons..."
          aria-label="Search the Academy"
          className="w-full h-9 pl-9 pr-14 text-sm bg-base-100 border border-base-300 rounded-lg outline-none focus:border-primary/50 transition-colors [&::-webkit-search-cancel-button]:hidden"
        />
        {query ? (
          <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-base-content/40 hover:text-base-content/70" title="Clear search">
            <XMarkIcon className="h-4 w-4" />
          </button>
        ) : (
          <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden md:inline-flex items-center rounded border border-base-300 bg-base-200 px-1.5 py-px text-[10px] font-medium text-base-content/50">⌘K</kbd>
        )}
      </div>

      {results ? (
        <div role="region" aria-live="polite" aria-label="Search results">
          {results.courses.length === 0 && results.lessons.length === 0 ? (
            <p className="m-0 px-2 py-4 text-xs text-base-content/60">Nothing matches "{query}".</p>
          ) : (
            <ul className="space-y-px">
              {results.courses.map((c) => (
                <li key={`c-${c.id}`}>
                  <button type="button" onClick={() => go(`/academy/${c.id}`)} className={quickLink}>
                    <CourseIcon icon={c.icon} className="size-4 shrink-0 text-base-content/60" />
                    <span className="flex-1 min-w-0 truncate font-medium">{c.title}</span>
                    <span className="text-[10px] uppercase tracking-wider text-base-content/40">Course</span>
                  </button>
                </li>
              ))}
              {results.lessons.map((l) => (
                <li key={`l-${l.id}`}>
                  <button type="button" onClick={() => go(lessonPath(l))} className={quickLink}>
                    <span className="w-4 shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{l.title}</span>
                      <span className="block truncate text-[11px] text-base-content/50">{COURSES.find((c) => c.id === l.courseId)?.title}</span>
                    </span>
                    <span className="text-[11px] tabular-nums text-base-content/50">{l.minutes} min</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          <section>
            <h2 className="m-0 px-2 mb-1 text-[11px] font-semibold uppercase tracking-wider text-base-content/50">Courses</h2>
            <ul className="space-y-px">
              {COURSES.map((c) => (
                <CourseRow key={c.id} course={c} active={c.id === activeCourseId} activeLessonId={activeLessonId} onNavigate={onNavigate} />
              ))}
            </ul>
          </section>

          <section>
            <h2 className="m-0 px-2 mb-1 text-[11px] font-semibold uppercase tracking-wider text-base-content/50">Quick links</h2>
            <ul className="space-y-px">
              <li>
                <button type="button" onClick={() => go("/academy#whats-new")} className={quickLink}>
                  <SparklesIcon className="size-4 shrink-0 text-blue-500" />
                  <span className="flex-1">New since you joined</span>
                  {newCount > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-blue-500 text-white text-[10px] font-semibold tabular-nums">{newCount}</span>}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => go("/academy#samples")} className={quickLink}>
                  <MusicalNoteIcon className="size-4 shrink-0 text-base-content/60" />
                  <span className="flex-1">Sample files</span>
                  {sampleCount > 0 && <span className="text-[11px] tabular-nums text-base-content/50">{sampleCount}</span>}
                </button>
              </li>
              {walkthrough && (
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      onNavigate?.();
                      if (walkthroughReady && walkthrough.tourId) openWalkthrough(walkthrough.tourId);
                      else navigate(lessonPath(walkthrough));
                    }}
                    className={quickLink}
                  >
                    <CursorArrowRaysIcon className="size-4 shrink-0 text-base-content/60" />
                    <span className="flex-1 min-w-0 truncate">Start the walkthrough</span>
                  </button>
                </li>
              )}
            </ul>
          </section>
        </>
      )}
    </nav>
  );
};

export default AcademyNavigator;
